import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosClientSignIn } from "../axios/api";

// Async thunk to fetch documents in a department/folder
export const fetchDocuments = createAsyncThunk(
  'documents/fetchDocuments',
  async ({ departmentId, folderPath = '', page = 1, search = '', fileType = '' }, { rejectWithValue }) => {
    try {
      const cleanDeptId = departmentId && typeof departmentId === 'object' ? (departmentId._id || departmentId.id) : departmentId;
      
      let response;
      if (cleanDeptId) {
        response = await axiosClientSignIn.get('/file/list/department-folder', {
          params: {
            path: folderPath,
            departmentId: cleanDeptId
          }
        });
      } else {
        response = await axiosClientSignIn.get('/file/list/folder', {
          params: {
            path: folderPath
          }
        });
      }
      
      console.log(response.data, "response.data from fetchDocuments");
      
      const items = response.data?.data?.items || [];
      const folders = [];
      const documents = [];
      
      items.forEach(item => {
        if (item.type === 'folder') {
          const cleanPath = item.fullPath && item.fullPath.endsWith('/') 
            ? item.fullPath.slice(0, -1) 
            : item.fullPath;
          folders.push(cleanPath);
        } else if (item.type === 'file') {
          documents.push({
            _id: item.id,
            fileName: item.fileName,
            originalName: item.originalName || item.fileName,
            s3Key: item.s3Key,
            s3Url: item.s3Url,
            fileSize: item.size,
            mimeType: item.mimeType,
            department: item.department,
            uploadedBy: item.uploadedBy,
            isPublic: item.isPublic,
            createdAt: item.createdAt,
            type: 'file'
          });
        }
      });
      
      return {
        documents,
        folders,
        pagination: response.data?.data?.pagination || {
          current: page,
          pages: 1,
          total: documents.length,
          hasNext: false,
          hasPrev: false
        }
      };
    } catch (error) {
      console.log(error, "error in fetchDocuments");
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch documents');
    }
  }
);

// Async thunk to fetch folders in a department
export const fetchFolders = createAsyncThunk(
  'documents/fetchFolders',
  async (departmentId, { getState, rejectWithValue }) => {
    try {
      return { folders: getState().documents.folders };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch folders');
    }
  }
);

// Async thunk to upload a document
export const uploadDocument = createAsyncThunk(
  'documents/uploadDocument',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post('/file/fileCreator', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const doc = response.data?.data?.document;
      if (!doc) {
        throw new Error('Upload succeeded but no document data returned');
      }
      
      const mappedDoc = {
        _id: doc.id,
        fileName: doc.fileName,
        originalName: doc.originalName || doc.fileName,
        s3Key: doc.s3Key,
        s3Url: doc.s3Url,
        fileSize: doc.size,
        mimeType: doc.mimeType,
        department: doc.department,
        uploadedBy: doc.uploadedBy,
        isPublic: doc.isPublic,
        createdAt: doc.createdAt,
        type: 'file'
      };
      
      return {
        document: mappedDoc
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Upload failed');
    }
  }
);

// Async thunk to bulk upload documents
export const bulkUploadDocuments = createAsyncThunk(
  'documents/bulkUploadDocuments',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post('/api/documents/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Bulk upload failed');
    }
  }
);

// Async thunk to get download URL
export const getDownloadUrl = createAsyncThunk(
  'documents/getDownloadUrl',
  async (documentId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get(`/api/documents/download/${documentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get download URL');
    }
  }
);

// Async thunk to delete a document
export const deleteDocument = createAsyncThunk(
  'documents/deleteDocument',
  async (documentId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.delete(`/api/documents/${documentId}`);
      return { documentId, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete document');
    }
  }
);

// Async thunk to create a folder
export const createFolder = createAsyncThunk(
  'documents/createFolder',
  async ({ departmentId, folderPath }, { rejectWithValue }) => {
    try {
      const cleanDeptId = departmentId && typeof departmentId === 'object' ? (departmentId._id || departmentId.id) : departmentId;
      
      // Extract parentPath and name from folderPath
      const parts = folderPath.split('/');
      const name = parts[parts.length - 1];
      const parentPath = parts.slice(0, -1).join('/');
      
      const response = await axiosClientSignIn.post('/file/create_folder', {
        departmentId: cleanDeptId,
        parentPath,
        name
      });
      
      const createdFolder = response.data?.folder;
      const cleanPath = createdFolder?.fullPath && createdFolder.fullPath.endsWith('/')
        ? createdFolder.fullPath.slice(0, -1)
        : (createdFolder?.fullPath || folderPath);
        
      return {
        folderPath: cleanPath
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create folder');
    }
  }
);

// Async thunk to move a document
export const moveDocument = createAsyncThunk(
  'documents/moveDocument',
  async ({ documentId, newFolderPath }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.put(`/api/documents/move/${documentId}`, {
        newFolderPath
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to move document');
    }
  }
);

// Async thunk to update document metadata
export const updateDocumentMetadata = createAsyncThunk(
  'documents/updateDocumentMetadata',
  async ({ documentId, metadata }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.put(`/api/documents/${documentId}`, metadata);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update document');
    }
  }
);

// Async thunk to update folder permissions
export const updateFolderPermissions = createAsyncThunk(
  'documents/updateFolderPermissions',
  async ({ folderId, folderPath, accessLevel, accessibleBy, accessibleDepartments }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post('/file/folder/permissions', {
        folderId,
        folderPath,
        accessLevel,
        accessibleBy,
        accessibleDepartments
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update folder permissions');
    }
  }
);

// Async thunk to update file permissions
export const updateFilePermissions = createAsyncThunk(
  'documents/updateFilePermissions',
  async ({ documentId, fileId, accessLevel, accessibleBy, accessibleDepartments, permissions, departmentPermissions, isPublic }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post('/file/document/permissions', {
        documentId,
        fileId,
        accessLevel,
        accessibleBy,
        accessibleDepartments,
        permissions,
        departmentPermissions,
        isPublic
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update file permissions');
    }
  }
);

// Async thunk to search folders and files
export const searchFoldersAndFiles = createAsyncThunk(
  'documents/searchFoldersAndFiles',
  async ({ query, departmentId, path = '' }, { rejectWithValue }) => {
    try {
      const cleanDeptId = departmentId && typeof departmentId === 'object' ? (departmentId._id || departmentId.id) : departmentId;
      const response = await axiosClientSignIn.get('/file/search', {
        params: {
          query,
          departmentId: cleanDeptId,
          path
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Search failed');
    }
  }
);


// Async thunk to get department statistics
export const getDepartmentStats = createAsyncThunk(
  'documents/getDepartmentStats',
  async (departmentId, { rejectWithValue }) => {
    try {
      const cleanDeptId = departmentId && typeof departmentId === 'object' ? (departmentId._id || departmentId.id) : departmentId;
      const response = await axiosClientSignIn.get(`/api/documents/stats/${cleanDeptId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch statistics');
    }
  }
);

// Async thunk to search documents (admin only)
export const searchDocuments = createAsyncThunk(
  'documents/searchDocuments',
  async ({ search, department, fileType, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const params = {
        search,
        page,
        limit,
        ...(department && { department }),
        ...(fileType && { fileType })
      };
      
      const response = await axiosClientSignIn.get('/api/documents/admin/search', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Search failed');
    }
  }
);

// Async thunk to get all departments
export const getAllDepartments = createAsyncThunk(
  'documents/getAllDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get('/api/documents/admin/departments');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch departments');
    }
  }
);

// Async thunk to get recent uploads
export const getRecentUploads = createAsyncThunk(
  'documents/getRecentUploads',
  async ({ departmentId, limit = 10 }, { rejectWithValue }) => {
    try {
      const cleanDeptId = departmentId && typeof departmentId === 'object' ? (departmentId._id || departmentId.id) : departmentId;
      const response = await axiosClientSignIn.get(`/api/documents/recent/${cleanDeptId}`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch recent uploads');
    }
  }
);

// Async thunk to get file type statistics
export const getFileTypeStats = createAsyncThunk(
  'documents/getFileTypeStats',
  async (departmentId, { rejectWithValue }) => {
    try {
      const cleanDeptId = departmentId && typeof departmentId === 'object' ? (departmentId._id || departmentId.id) : departmentId;
      const response = await axiosClientSignIn.get(`/api/documents/file-types/${cleanDeptId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch file type statistics');
    }
  }
);

// Async thunk to get document details by ID
export const getDocumentById = createAsyncThunk(
  'documents/getDocumentById',
  async (documentId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get(`/api/documents/${documentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch document details');
    }
  }
);

const initialState = {
  // Documents and folders
  documents: [],
  folders: [],
  currentDocument: null,
  
  // Navigation
  currentPath: '',
  breadcrumbs: [],
  
  // Pagination
  pagination: {
    current: 1,
    pages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false
  },
  
  // Statistics
  stats: null,
  fileTypeStats: [],
  recentUploads: [],
  
  // Departments
  departments: [],
  currentDepartment: null,
  
  // Search and filters
  searchTerm: '',
  selectedFilter: 'all',
  searchResults: [],
  
  // UI state
  loading: false,
  uploading: false,
  uploadProgress: 0,
  error: null,
  
  // Upload state
  uploadQueue: [],
  completedUploads: [],
  failedUploads: [],
  
  // Selection
  selectedDocuments: [],
  
  // Dialogs/modals
  uploadDialogOpen: false,
  folderDialogOpen: false,
  moveDialogOpen: false,
  deleteDialogOpen: false,
};

const documentSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    // UI Actions
    setCurrentPath: (state, action) => {
      state.currentPath = action.payload;
      state.breadcrumbs = action.payload ? action.payload.split('/') : [];
    },
    
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    
    setSelectedFilter: (state, action) => {
      state.selectedFilter = action.payload;
    },
    
    setCurrentDepartment: (state, action) => {
      const payload = action.payload;
      state.currentDepartment = payload && typeof payload === 'object'
        ? (payload._id || payload.id)
        : payload;
    },
    
    // Selection Actions
    toggleDocumentSelection: (state, action) => {
      const documentId = action.payload;
      const index = state.selectedDocuments.indexOf(documentId);
      if (index === -1) {
        state.selectedDocuments.push(documentId);
      } else {
        state.selectedDocuments.splice(index, 1);
      }
    },
    
    clearDocumentSelection: (state) => {
      state.selectedDocuments = [];
    },
    
    selectAllDocuments: (state) => {
      state.selectedDocuments = state.documents.map(doc => doc._id);
    },
    
    // Dialog Actions
    setUploadDialogOpen: (state, action) => {
      state.uploadDialogOpen = action.payload;
    },
    
    setFolderDialogOpen: (state, action) => {
      state.folderDialogOpen = action.payload;
    },
    
    setMoveDialogOpen: (state, action) => {
      state.moveDialogOpen = action.payload;
    },
    
    setDeleteDialogOpen: (state, action) => {
      state.deleteDialogOpen = action.payload;
    },
    
    // Upload Queue Management
    addToUploadQueue: (state, action) => {
      state.uploadQueue.push(...action.payload);
    },
    
    removeFromUploadQueue: (state, action) => {
      state.uploadQueue = state.uploadQueue.filter(item => item.id !== action.payload);
    },
    
    clearUploadQueue: (state) => {
      state.uploadQueue = [];
      state.completedUploads = [];
      state.failedUploads = [];
    },
    
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    
    // Error Management
    clearError: (state) => {
      state.error = null;
    },
    
    // Reset state
    resetDocuments: (state) => {
      state.documents = [];
      state.folders = [];
      state.pagination = initialState.pagination;
      state.selectedDocuments = [];
    },
  },
  
  extraReducers: (builder) => {
    builder
      // Fetch Documents
      .addCase(fetchDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload.documents;
        state.folders = action.payload.folders;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Folders
      .addCase(fetchFolders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Upload Document
      .addCase(uploadDocument.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.uploading = false;
        state.documents.unshift(action.payload.document);
        state.completedUploads.push(action.payload.document);
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload;
        state.failedUploads.push({ error: action.payload });
      })
      
      // Bulk Upload Documents
      .addCase(bulkUploadDocuments.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(bulkUploadDocuments.fulfilled, (state, action) => {
        state.uploading = false;
        if (action.payload.results) {
          action.payload.results.forEach(result => {
            state.documents.unshift(result.document);
            state.completedUploads.push(result.document);
          });
        }
        if (action.payload.errors) {
          state.failedUploads.push(...action.payload.errors);
        }
      })
      .addCase(bulkUploadDocuments.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload;
      })
      
      // Delete Document
      .addCase(deleteDocument.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = state.documents.filter(doc => doc._id !== action.payload.documentId);
        state.selectedDocuments = state.selectedDocuments.filter(id => id !== action.payload.documentId);
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create Folder
      .addCase(createFolder.pending, (state) => {
        state.loading = true;
      })
      .addCase(createFolder.fulfilled, (state, action) => {
        state.loading = false;
        if (!state.folders.includes(action.payload.folderPath)) {
          state.folders.push(action.payload.folderPath);
        }
      })
      .addCase(createFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Move Document
      .addCase(moveDocument.pending, (state) => {
        state.loading = true;
      })
      .addCase(moveDocument.fulfilled, (state, action) => {
        state.loading = false;
        const docIndex = state.documents.findIndex(doc => doc._id === action.payload.document._id);
        if (docIndex !== -1) {
          state.documents[docIndex] = action.payload.document;
        }
      })
      .addCase(moveDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Document Metadata
      .addCase(updateDocumentMetadata.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDocumentMetadata.fulfilled, (state, action) => {
        state.loading = false;
        const docIndex = state.documents.findIndex(doc => doc._id === action.payload.document._id);
        if (docIndex !== -1) {
          state.documents[docIndex] = action.payload.document;
        }
        if (state.currentDocument && state.currentDocument._id === action.payload.document._id) {
          state.currentDocument = action.payload.document;
        }
      })
      .addCase(updateDocumentMetadata.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Get Department Stats
      .addCase(getDepartmentStats.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDepartmentStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.stats;
      })
      .addCase(getDepartmentStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Search Documents
      .addCase(searchDocuments.pending, (state) => {
        state.loading = true;
      })
      .addCase(searchDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload.documents;
        state.pagination = action.payload.pagination;
      })
      .addCase(searchDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Get All Departments
      .addCase(getAllDepartments.fulfilled, (state, action) => {
        state.departments = action.payload.departments;
      })
      
      // Get Recent Uploads
      .addCase(getRecentUploads.fulfilled, (state, action) => {
        state.recentUploads = action.payload.documents;
      })
      
      // Get File Type Stats
      .addCase(getFileTypeStats.fulfilled, (state, action) => {
        state.fileTypeStats = action.payload.fileTypes;
      })
      
      // Get Document By ID
      .addCase(getDocumentById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDocumentById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDocument = action.payload.document;
      })
      .addCase(getDocumentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Folder Permissions
      .addCase(updateFolderPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFolderPermissions.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateFolderPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update File Permissions
      .addCase(updateFilePermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFilePermissions.fulfilled, (state, action) => {
        state.loading = false;
        const doc = action.payload?.document;
        if (doc) {
          const idx = state.documents.findIndex(d => d._id === doc.id || d._id === doc._id);
          if (idx !== -1) {
            state.documents[idx] = {
              ...state.documents[idx],
              accessLevel: doc.accessLevel,
              isPublic: doc.isPublic,
              permissions: doc.permissions,
              departmentPermissions: doc.departmentPermissions
            };
          }
        }
      })
      .addCase(updateFilePermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Search Folders & Files
      .addCase(searchFoldersAndFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchFoldersAndFiles.fulfilled, (state, action) => {
        state.loading = false;
        const items = action.payload?.data?.items || [];
        const folders = [];
        const documents = [];
        
        items.forEach(item => {
          if (item.type === 'folder') {
            folders.push({
              _id: item.id,
              id: item.id,
              name: item.name,
              fullPath: item.fullPath,
              level: item.level,
              department: item.department,
              createdAt: item.createdAt,
              type: 'folder',
              itemCount: item.itemCount || 0,
              accessLevel: item.accessLevel || 'DEPARTMENT',
              accessibleBy: item.accessibleBy || [],
              accessibleDepartments: item.accessibleDepartments || []
            });
          } else if (item.type === 'file') {
            documents.push({
              _id: item.id,
              id: item.id,
              fileName: item.fileName,
              originalName: item.originalName || item.fileName,
              s3Key: item.s3Key,
              s3Url: item.s3Url,
              fileSize: item.size,
              size: item.size,
              mimeType: item.mimeType,
              department: item.department,
              uploadedBy: item.uploadedBy,
              isPublic: item.isPublic,
              accessLevel: item.accessLevel || 'DEPARTMENT',
              permissions: item.permissions || [],
              departmentPermissions: item.departmentPermissions || [],
              accessibleBy: item.accessibleBy || [],
              accessibleDepartments: item.accessibleDepartments || [],
              createdAt: item.createdAt,
              type: 'file'
            });
          }
        });
        
        state.documents = documents;
        state.folders = folders;
        state.pagination = {
          current: 1,
          pages: 1,
          total: items.length,
          hasNext: false,
          hasPrev: false
        };
      })
      .addCase(searchFoldersAndFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setCurrentPath,
  setSearchTerm,
  setSelectedFilter,
  setCurrentDepartment,
  toggleDocumentSelection,
  clearDocumentSelection,
  selectAllDocuments,
  setUploadDialogOpen,
  setFolderDialogOpen,
  setMoveDialogOpen,
  setDeleteDialogOpen,
  addToUploadQueue,
  removeFromUploadQueue,
  clearUploadQueue,
  setUploadProgress,
  clearError,
  resetDocuments
} = documentSlice.actions;

export default documentSlice.reducer;
